import { ConfigProvider, theme } from "antd";
import Container from "./Components/Container";
import { ProfileScreeningProvider } from "./Context/ProfileScreeningContext";
import { UserOnboardingProvider } from "./Context/UserOnboardingContext";
import { useLoader } from "./Context/LoaderContext";
import { NotificationProvider } from "./Context/NotificationContext";
import { useTheme } from "./Context/ThemeContext";
import { SiderProvider } from "./Context/SiderContext";
import Routing from "./Routing";
import AuthService from "./Service/auth.service";
import organizationService from "./Service/organization.service";
import { useEffect, useState } from "react";
import { TourProvider } from "./Context/TourContext";
import { AccessProvider } from "./Context/AccessContext";
import Tour from "./Components/Tour";
import Loader from "./Components/Loader";
import AILoader from "./Components/AILoader";
import { useAILoader } from "./Context/AILoaderContext";
import PWAInstallPrompt from "./Components/PWAInstallPrompt";
import OfflineIndicator from "./Components/OfflineIndicator";
import { PWAProvider } from "./Context/PWAContext";
import useViewportHeight from "./Hooks/useViewportHeight";
import { SampleQuestionsProvider } from "./Context/SampleQuestionsContext";
import { Analytics } from "./Components/Analytics";
import "./index.scss";
import { useLocation } from "react-router-dom";
const DEFAULT_PRIMARY = "#001529";
const DEFAULT_PRIMARY_BG = "#f0f8ff";

function App() {
  useViewportHeight();
  const { pathname } = useLocation();
  const { isLoading, loadingMessage } = useLoader();
  const { isAILoaderLoading, AILoaderMessage } = useAILoader();

  const { defaultAlgorithm } = theme;
  const { customTheme } = useTheme();
  const [currentUserCheckComplete, setCurrentUserCheckComplete] =
    useState(false);

  const isProfileScreeningRoute = pathname === "/profile-screening";
  const useDefaultTheme = isProfileScreeningRoute;

  const customisedTheme = {
    algorithm: defaultAlgorithm,
    token: {
      colorPrimary: useDefaultTheme
        ? DEFAULT_PRIMARY
        : customTheme
          ? customTheme?.primaryColor
          : theme.useToken().token.colorPrimary,
      colorPrimaryBg: useDefaultTheme
        ? DEFAULT_PRIMARY_BG
        : customTheme
          ? customTheme?.secondaryColor
          : theme.useToken().token.colorPrimaryBg,
      fontFamily: "Inter",
      fontFamilyCode: "JetBrains Mono",
    },
    components: {
      Tabs: {
        horizontalItemPadding: "0px 40px",
      },
      Typography: {
        titleMarginTop: 0,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
      Button: {
        primaryShadow: "none",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
  };


  const checkForLoggedInUser = async () => {
    // Skip auth check for public routes
    if (pathname === "/profile-screening") {
      setCurrentUserCheckComplete(true);
      return;
    }

    await AuthService.getInstance().checkTokenAndRestoreCurrentUser();
    const user = AuthService.getInstance().getCurrentUser();
    if (user) {
      // Trigger organization data fetch to load theme immediately
      await organizationService.getOrganizationData();
    }
    setCurrentUserCheckComplete(true);
  };

  useEffect(() => {
     // Skip auth check for public routes that don't require authentication
     if (pathname === "/profile-screening") {
      setCurrentUserCheckComplete(true);
      return;
    }

    checkForLoggedInUser();

    // Subscribe to user sign in event
    const handleUserSignIn = () => {
      // Trigger organization data fetch to load theme immediately
      organizationService.getOrganizationData();
    };
    AuthService.getInstance().subscribe(
      AuthService.EVENTS.USER_SIGNED_IN,
      handleUserSignIn
    );

    return () => {
      AuthService.getInstance().unsubscribe(
        AuthService.EVENTS.USER_SIGNED_IN,
        handleUserSignIn
      );
    };
  }, [pathname]);

  return (
    <>
      <Analytics />
      <ProfileScreeningProvider>
      <UserOnboardingProvider>
    <AccessProvider>
    <NotificationProvider>
      <ConfigProvider theme={customisedTheme}>
        <SampleQuestionsProvider>
          <PWAProvider>
            <SiderProvider>
              <OfflineIndicator />
              <Loader isLoading={isLoading} loadingMessage={loadingMessage} />
              <AILoader
                AILoaderMessage={AILoaderMessage}
                isAILoaderLoading={isAILoaderLoading}
              />
              <TourProvider>
                <Container>{currentUserCheckComplete && <Routing />}</Container>
                <Tour />
                <PWAInstallPrompt />
              </TourProvider>
            </SiderProvider>
          </PWAProvider>
        </SampleQuestionsProvider>
      </ConfigProvider>
    </NotificationProvider>
    </AccessProvider>
    </UserOnboardingProvider>
    </ProfileScreeningProvider>
    </>
  );
}

export default App;
